import dayjs from "dayjs";
import { ParsedResultSet } from "../workout/utils";
import {
  ExerciseTypeReps,
  ExerciseTypeSet,
  Units,
  UnitsTime,
} from "../../constants";
import { ExerciseChartInfo } from "./charts/VolumeChart";
import { getNavIndexForDate, getTimeRange } from "../../utils/date";
import { TimeRangeUnit } from "./components/TimeRangeButtons";
import { Highlights, HighlightType, Unit } from "./components/HighlightItem";
import { unitSetLabelCoach } from "../../utils/units";
import React from "react";
import {
  Dumbbell,
  Repeat,
  Ruler,
  ChartNoAxesColumn as VolumeIcon,
  Timer,
} from "lucide-react";

// aggregate exercise sets to calculate total volume and sets completed
export const aggregateExerciseSets = (sets: ParsedResultSet[]) => {
  if (!sets?.length) return { volume: 0, sets: 0 };

  let totalVolume = 0;
  let totalReps = 0;
  let totalWeight = 0;

  for (const rs of sets) {
    for (const set of rs.sets) {
      if (!set.checked) continue;

      const reps =
        set.typeReps === ExerciseTypeReps.RANGE
          ? Number(set.completedReps ?? 0)
          : Number(set.reps ?? 0);

      const weight = Number(set.weight ?? 0);
      const volume = reps * weight;

      totalVolume += volume;
      totalReps += reps;
      totalWeight += weight;
    }
  }

  return { volume: totalVolume, reps: totalReps, weight: totalWeight };
};

export const filterResultsByTimeRange = <
  T extends { activity: { completedAt: string } },
>(
  results: T[],
  timeRange: { start: dayjs.Dayjs; end: dayjs.Dayjs } | undefined,
) => {
  if (!timeRange) return results;

  const { start, end } = timeRange;

  return results.filter((r) => {
    const completedAt = dayjs(r.activity.completedAt);
    return completedAt.isAfter(start) && completedAt.isBefore(end);
  });
};

// build data for volume chart
export function buildExerciseVolumes(filteredExerciseSets: ParsedResultSet[]) {
  const volumeByDay: Record<
    string,
    {
      volume: number;
      minWeight: number;
      maxWeight: number;
      minReps: number;
      maxReps: number;
    }
  > = {};

  filteredExerciseSets.forEach((result) => {
    const dateKey = dayjs(result.completedAt).format("YYYY-MM-DD");

    result.sets.forEach((set) => {
      if (!set.checked) return;

      const reps =
        set.typeReps === ExerciseTypeReps.RANGE
          ? Number(set.completedReps || 0)
          : Number(set.reps || 0);
      const weight = Number(set.weight || 0);
      const setVolume = weight * reps;

      // Initialize or update the day's volume and min/max values
      // since we can have multiple sets in a day
      const day = volumeByDay[dateKey] || {
        volume: 0,
        minWeight: weight,
        maxWeight: weight,
        minReps: reps,
        maxReps: reps,
      };

      day.volume += setVolume;
      day.minWeight = Math.min(day.minWeight, weight);
      day.maxWeight = Math.max(day.maxWeight, weight);
      day.minReps = Math.min(day.minReps, reps);
      day.maxReps = Math.max(day.maxReps, reps);

      volumeByDay[dateKey] = day;
    });
  });

  return Object.entries(volumeByDay).map(
    ([date, { volume, minWeight, maxWeight, minReps, maxReps }]) => ({
      date,
      volume,
      weight: { min: minWeight, max: maxWeight },
      reps: { min: minReps, max: maxReps },
    }),
  ) as ExerciseChartInfo[];
}

// calculate current and previous results based on selected time range
export function getCurrentAndPrevParsedSets(
  parsedSets: ParsedResultSet[],
  activeButton: TimeRangeUnit,
  navIndex: number,
) {
  const currentRange =
    activeButton === TimeRangeUnit.ALL_TIME
      ? undefined
      : getTimeRange(activeButton, navIndex);
  const prevRange =
    activeButton === TimeRangeUnit.ALL_TIME
      ? undefined
      : getTimeRange(activeButton, navIndex - 1);

  const filterByRange = (
    sets: ParsedResultSet[],
    range?: { start: dayjs.Dayjs; end: dayjs.Dayjs },
  ) =>
    range
      ? sets.filter((s) => {
          const completed = dayjs(s.completedAt);
          return (
            completed.isSameOrAfter(range.start, "day") &&
            completed.isSameOrBefore(range.end, "day")
          );
        })
      : sets;

  return {
    currentResults: filterByRange(parsedSets, currentRange),
    prevResults: prevRange ? filterByRange(parsedSets, prevRange) : [],
  };
}

const highlightMetricIcon = (exerciseTypeSet: ExerciseTypeSet) => {
  switch (exerciseTypeSet) {
    case ExerciseTypeSet.WEIGHT:
      return React.createElement(Dumbbell);
    case ExerciseTypeSet.TIME:
      return React.createElement(Timer);
    case ExerciseTypeSet.DISTANCE:
      return React.createElement(Ruler);
    default:
      return React.createElement(Dumbbell);
  }
};

export function buildHighlights(
  sets: ParsedResultSet[],
  prevSets: ParsedResultSet[],
  units: Units | UnitsTime,
  exerciseTypeSet: ExerciseTypeSet | null,
  showDiffs = true,
  isCurrentMonth = false,
): Highlights {
  const currentValues = aggregateExerciseSets(sets);
  const prevValues = aggregateExerciseSets(prevSets);

  const metricIcon = highlightMetricIcon(exerciseTypeSet);

  return {
    volume: {
      type: HighlightType.VOLUME,
      icon: React.createElement(VolumeIcon),
      value: {
        current: currentValues.volume,
        previous: prevValues.volume,
        unit: unitSetLabelCoach(units, exerciseTypeSet) as Unit,
      },
      showDiffs: showDiffs,
      isCurrentMonth,
    },
    reps: {
      type: HighlightType.REPS,
      icon: React.createElement(Repeat),
      value: {
        current: currentValues.reps,
        previous: prevValues.reps,
        unit: "reps" as Unit,
      },
      showDiffs: showDiffs,
      isCurrentMonth,
    },
    metric: {
      type:
        exerciseTypeSet === ExerciseTypeSet.TIME
          ? HighlightType.TIME
          : HighlightType.METRIC,
      icon: metricIcon,
      value: {
        current: currentValues.weight,
        previous: prevValues.weight,
        unit: unitSetLabelCoach(units, exerciseTypeSet) as Unit,
      },
      showDiffs: showDiffs,
      isCurrentMonth,
    },
  };
}

const DEFAULT_TIME_RANGE_ORDER = [
  TimeRangeUnit.MONTH,
  TimeRangeUnit.QUARTER,
  TimeRangeUnit.YEAR,
  TimeRangeUnit.ALL_TIME,
];

export const findDefaultTimeRange = <
  T extends { completedAt: string | Date },
>(params: {
  maxDate: dayjs.Dayjs | null;
  records: T[];
  minEntries: number;
  defaultUnit: TimeRangeUnit;
  timeRangeOrder?: TimeRangeUnit[];
}) => {
  const {
    maxDate,
    records,
    minEntries,
    defaultUnit,
    timeRangeOrder = DEFAULT_TIME_RANGE_ORDER,
  } = params;

  if (!maxDate) {
    return { unit: defaultUnit, index: 0 };
  }

  for (const unit of timeRangeOrder) {
    const index = getNavIndexForDate(unit, maxDate);
    const { start, end } = getTimeRange(unit, index);

    const count = records.filter((r) => {
      const date = dayjs(r.completedAt);
      return (
        (!start || date.isSameOrAfter(start, "day")) &&
        (!end || date.isSameOrBefore(end, "day"))
      );
    }).length;

    if (count >= minEntries || unit === TimeRangeUnit.ALL_TIME) {
      return { unit, index };
    }
  }

  return { unit: defaultUnit, index: 0 };
};

export const NO_ACTIVITY_EXERCISE_MESSAGE =
  "No activity recorded for the selected dates";
